import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '../store/sessionStore';
import { useAuthStore } from '../store/authStore';
import { wsService } from '../services/websocket';
import { api } from '../services/api';
import { Chatter } from '../types';
import { formatDistanceToNow } from 'date-fns';
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  CheckIcon,
  XMarkIcon,
  ChartBarIcon,
  ChatBubbleLeftIcon,
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const {
    sessions,
    currentSession,
    chatters,
    stats,
    isLoading,
    fetchSessions,
    createSession,
    updateSession,
    setCurrentSession,
    toggleChatterGreeted,
    addChatter,
    updateChatter,
    fetchChatters,
  } = useSessionStore();

  const [newSessionName, setNewSessionName] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [showNewSessionForm, setShowNewSessionForm] = useState(false);
  const [recentlyGreeted, setRecentlyGreeted] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [userSettings, setUserSettings] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    sessionId: string;
    sessionName: string;
    chatterCount: number;
  }>({
    isOpen: false,
    sessionId: '',
    sessionName: '',
    chatterCount: 0
  });
  
  // Calculate pagination for chatters
  const sortedChatters = chatters.sort((a, b) => new Date(a.firstSeenAt).getTime() - new Date(b.firstSeenAt).getTime());
  const totalPages = Math.ceil(sortedChatters.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedChatters = sortedChatters.slice(startIndex, endIndex);

  // Reset to page 1 when pageSize changes or session changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, currentSession]);

  // Auto-populate session name with user's channel when form opens
  const handleShowNewSessionForm = async () => {
    if (user?.username && !newSessionName) {
      setNewSessionName(`${user.username} Stream`);
    }
    
    // Load user settings to get default keywords
    if (!userSettings) {
      await loadUserSettings();
    }
    
    // Auto-populate keywords from default keywords
    if (userSettings?.defaultKeywords) {
      const defaultKeywords = parseDefaultKeywords(userSettings.defaultKeywords);
      setKeywords(defaultKeywords);
    }
    
    setShowNewSessionForm(true);
  };

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Auto-select session logic
  useEffect(() => {
    // Only auto-select if no session is currently selected and we have sessions
    if (!currentSession && sessions.length > 0) {
      // Find active sessions
      const activeSessions = sessions.filter(s => s.isActive);
      
      let sessionToSelect = null;
      
      if (activeSessions.length === 1) {
        // If there's exactly one active session, select it
        sessionToSelect = activeSessions[0];
      } else if (activeSessions.length > 1) {
        // If there are multiple active sessions, select the newest one (by createdAt)
        sessionToSelect = activeSessions.reduce((newest, current) => {
          return new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest;
        });
      }
      
      // Auto-select the session if we found one
      if (sessionToSelect) {
        setCurrentSession(sessionToSelect);
        fetchChatters(sessionToSelect.id);
        
        // Join the session to receive real-time updates
        wsService.joinSession(sessionToSelect.id);
      }
    }
  }, [sessions, currentSession, setCurrentSession, fetchChatters]);

  useEffect(() => {
    // WebSocket event listeners
    const handleNewChatter = (chatter: Chatter) => {
      addChatter(chatter);
      if (user?.settings?.soundNotifications) {
        // Play notification sound
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {
          // Ignore if audio playback fails
        });
      }
      toast.success(`New chatter: ${chatter.displayName}`, {
        duration: 3000,
      });
    };

    const handleChatterUpdate = (chatter: Chatter) => {
      updateChatter(chatter);
    };

    const handleUserApproved = (payload: any) => {
      toast.success(payload.message || 'Your account has been approved! You can now start sessions.');
      // Refresh user data to update approval status
      window.location.reload();
    };

    const handleUserRejected = (payload: any) => {
      toast.error(payload.message || 'Your account has been rejected.');
      // Refresh user data to update approval status
      window.location.reload();
    };

    wsService.on('new_chatter', handleNewChatter);
    wsService.on('chatter_update', handleChatterUpdate);
    wsService.on('user_approved', handleUserApproved);
    wsService.on('user_rejected', handleUserRejected);

    return () => {
      wsService.off('new_chatter', handleNewChatter);
      wsService.off('chatter_update', handleChatterUpdate);
      wsService.off('user_approved', handleUserApproved);
      wsService.off('user_rejected', handleUserRejected);
    };
  }, [addChatter, updateChatter, user?.settings?.soundNotifications]);

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSessionName.trim() || !user) return;

    try {
      const newSession = await createSession(newSessionName, stringifySessionKeywords(keywords));
      
      // Automatically start the WebSocket connection for the new session
      wsService.startSession(newSession.id, parseSessionKeywords(newSession.keywords));
      toast.success(`Session created and started! Monitoring ${user.username}'s chat`);
      
      setNewSessionName('');
      setKeywords([]);
      setKeywordInput('');
      setShowNewSessionForm(false);
    } catch (error: any) {
      if (error.response?.data?.error === 'Account not approved') {
        toast.error('Your account needs to be approved by an admin before you can start sessions.');
      } else {
        toast.error('Failed to create session');
      }
    }
  };

  const handleSelectSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;

    try {
      setCurrentSession(session);
      await fetchChatters(sessionId);
      
      // Join the session to receive real-time updates
      wsService.joinSession(sessionId);
      
      toast.success(`Selected session: ${session.name}`);
    } catch (error) {
      toast.error('Failed to load session data');
    }
  };

  const handleToggleGreet = async (chatterId: string, isGreeted: boolean) => {
    if (isGreeted) {
      // Add to recently greeted for animation
      setRecentlyGreeted(prev => new Set(prev).add(chatterId));
      
      // Remove from recently greeted after 2 seconds
      setTimeout(() => {
        setRecentlyGreeted(prev => {
          const newSet = new Set(prev);
          newSet.delete(chatterId);
          return newSet;
        });
      }, 2000);
    }
    
    await toggleChatterGreeted(chatterId, isGreeted);
  };

  const handleStartSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session || !user) return;

    try {
      await updateSession(sessionId, { isActive: true });
      setCurrentSession(session);
      // Start the session (backend will use session owner's username)
      wsService.startSession(sessionId, parseSessionKeywords(session.keywords));
      toast.success(`Session started! Monitoring ${session.user.username}'s chat`);
    } catch (error: any) {
      if (error.response?.data?.error === 'Only session owners can start sessions') {
        toast.error('Only session owners can start sessions. Editors can view and manage chatters.');
      } else {
        toast.error('Failed to start session');
      }
    }
  };

  const handleStopSession = async () => {
    if (!currentSession) return;

    try {
      await updateSession(currentSession.id, { isActive: false });
      setCurrentSession(null);
      wsService.stopSession();
      toast.success('Session stopped!');
    } catch (error) {
      toast.error('Failed to stop session');
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  const handleKeywordInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  const handleDeleteSessionClick = (sessionId: string, sessionName: string, chatterCount: number) => {
    setDeleteConfirm({
      isOpen: true,
      sessionId,
      sessionName,
      chatterCount
    });
  };

  const handleDeleteSession = async () => {
    try {
      const result = await api.delete(`/api/sessions/${deleteConfirm.sessionId}`);
      
      // If this was the current session, stop it
      if (currentSession?.id === deleteConfirm.sessionId) {
        setCurrentSession(null);
        wsService.stopSession();
      }
      
      // Refresh sessions list
      await fetchSessions();
      
      toast.success(`Session "${result.data.sessionName}" deleted successfully. Removed ${result.data.deletedChatters} chatters.`);
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session');
    } finally {
      setDeleteConfirm({
        isOpen: false,
        sessionId: '',
        sessionName: '',
        chatterCount: 0
      });
    }
  };

  // Helper functions to handle session keywords as JSON string
  const parseSessionKeywords = (jsonString: string): string[] => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  const stringifySessionKeywords = (keywords: string[]): string => {
    return JSON.stringify(keywords);
  };

  // Helper function to parse default keywords
  const parseDefaultKeywords = (jsonString: string): string[] => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  // Load user settings
  const loadUserSettings = async () => {
    try {
      const response = await api.get('/api/settings');
      setUserSettings(response.data);
    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  };

  // Helper function to check if current user is session owner
  const isSessionOwner = (session: any) => {
    return session.userId === user?.id;
  };

  // Ensure only one session is joined at a time
  useEffect(() => {
    return () => {
      // Leave the previous session when switching
      wsService.leaveSession();
    };
  }, [currentSession]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white light:text-gray-900 mb-2">{t('dashboard.title')}</h1>
          <p className="text-gray-400 light:text-gray-600">{t('dashboard.description')}</p>
        </div>

        {/* Approval Status Banner */}
        {user && !user.isApproved && (
          <div className="mb-6 p-4 bg-yellow-900 border border-yellow-700 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-yellow-200">
                  {t('dashboard.approval.pendingTitle')}
                </h3>
                <p className="text-sm text-yellow-300 mt-1">
                  {t('dashboard.approval.pendingMessage')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-twitch-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">{t('dashboard.totalSessions')}</dt>
                  <dd className="text-lg font-medium text-white light:text-gray-900">{sessions.length}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PlayIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">{t('dashboard.activeSessions')}</dt>
                  <dd className="text-lg font-medium text-white light:text-gray-900">
                    {sessions.filter(s => s.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChatBubbleLeftIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">{t('dashboard.totalChatters')}</dt>
                  <dd className="text-lg font-medium text-white light:text-gray-900">{stats.total}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">{t('dashboard.greeted')}</dt>
                  <dd className="text-lg font-medium text-white light:text-gray-900">{stats.greeted}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sessions */}
          <div>
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white light:text-gray-900">{t('dashboard.sessions')}</h2>
                <button
                  onClick={handleShowNewSessionForm}
                  disabled={!user?.isApproved}
                  className={`btn-primary ${!user?.isApproved ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={!user?.isApproved ? t('dashboard.approval.needsApproval') : ''}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('dashboard.newSession')}
                </button>
              </div>

              {/* New Session Form */}
              {showNewSessionForm && (
                <form onSubmit={handleCreateSession} className="mb-6 p-4 bg-gray-700 light:bg-gray-100 rounded-lg">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 light:text-gray-700 mb-1">
                        {t('dashboard.sessionName')}
                      </label>
                      <input
                        type="text"
                        value={newSessionName}
                        onChange={(e) => setNewSessionName(e.target.value)}
                        className="input w-full"
                        placeholder={t('dashboard.sessionNamePlaceholder')}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 light:text-gray-700 mb-1">
                        {t('dashboard.keywords')}
                      </label>
                      <div className="flex space-x-2 mb-2">
                        <input
                          type="text"
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyPress={handleKeywordInputKeyPress}
                          className="input flex-1"
                          placeholder={t('dashboard.addKeywordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={addKeyword}
                          className="btn-secondary"
                        >
                          {t('common.add')}
                        </button>
                      </div>
                      {keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {keywords.map((keyword) => (
                            <span
                              key={keyword}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-twitch-600 text-white"
                            >
                              {keyword}
                              <button
                                type="button"
                                onClick={() => removeKeyword(keyword)}
                                className="ml-1 text-twitch-300 hover:text-white"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <button
                        type="submit"
                        className="btn-primary flex-1"
                      >
                        {t('common.create')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowNewSessionForm(false)}
                        className="btn-secondary flex-1"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Sessions List */}
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`p-3 rounded-lg border transition-colors duration-200 ${
                      currentSession?.id === session.id
                        ? 'bg-twitch-900 border-twitch-600 light:bg-twitch-50 light:border-twitch-300'
                        : session.isActive
                        ? 'bg-green-900 border-green-700 light:bg-green-50 light:border-green-300'
                        : 'bg-gray-700 border-gray-600 hover:border-gray-500 light:bg-gray-50 light:border-gray-300 light:hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/session/${session.id}`}
                            className="font-medium text-white light:text-gray-900 hover:text-twitch-400 light:hover:text-twitch-600 transition-colors truncate"
                          >
                            {session.name}
                          </Link>
                          {session.isActive && (
                            <span className="flex-shrink-0 w-2 h-2 bg-green-400 rounded-full" title={t('dashboard.activeSession')}></span>
                          )}
                          {currentSession?.id === session.id && (
                            <span className="flex-shrink-0 text-xs bg-twitch-600 text-white px-2 py-0.5 rounded-full">{t('dashboard.selected')}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 light:text-gray-600 truncate">
                          {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}
                        </p>
                        {parseSessionKeywords(session.keywords).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {parseSessionKeywords(session.keywords).slice(0, 3).map((keyword) => (
                              <span
                                key={keyword}
                                className="inline-block px-1 py-0.5 rounded text-xs bg-gray-600 text-gray-300 light:bg-gray-200 light:text-gray-700"
                              >
                                {keyword}
                              </span>
                            ))}
                            {parseSessionKeywords(session.keywords).length > 3 && (
                              <span className="inline-block px-1 py-0.5 rounded text-xs bg-gray-600 text-gray-400 light:bg-gray-200 light:text-gray-600">
                                +{parseSessionKeywords(session.keywords).length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-1 ml-2">
                        {/* Select Session Button */}
                        {currentSession?.id !== session.id && (
                          <button
                            onClick={() => handleSelectSession(session.id)}
                            className="p-2 text-gray-400 light:text-gray-600 hover:text-twitch-400 light:hover:text-twitch-600"
                            title={t('dashboard.selectThisSession')}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Start/Stop Button - Only for session owners */}
                        {isSessionOwner(session) && (
                          session.isActive ? (
                            <button
                              onClick={handleStopSession}
                              className="p-2 text-red-400 hover:text-red-300 light:text-red-600 light:hover:text-red-700"
                              title={t('dashboard.stopSession')}
                            >
                              <StopIcon className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStartSession(session.id)}
                              className="p-2 text-green-400 hover:text-green-300 light:text-green-600 light:hover:text-green-700"
                              title={t('dashboard.startSession')}
                            >
                              <PlayIcon className="h-4 w-4" />
                            </button>
                          )
                        )}
                        
                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeleteSessionClick(session.id, session.name, session.chatters?.length || 0)}
                          className="p-2 text-gray-400 light:text-gray-600 hover:text-red-400 light:hover:text-red-600"
                          title={t('dashboard.deleteSession')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {sessions.length === 0 && (
                <div className="text-center text-gray-400 light:text-gray-600 py-8">
                  <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t('dashboard.noSessionsYet')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Chatters */}
          <div className="lg:col-span-2">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white light:text-gray-900">
                  {t('dashboard.chatters')} {currentSession && `- ${currentSession.name} (${sortedChatters.length})`}
                </h2>
                <div className="flex items-center space-x-3">
                  {currentSession && sortedChatters.length > 0 && (
                    <div className="flex items-center space-x-2 text-sm">
                      <label className="text-gray-300 light:text-gray-700">{t('dashboard.show')}:</label>
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="bg-gray-700 light:bg-white border border-gray-600 light:border-gray-300 rounded px-2 py-1 text-white light:text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-twitch-500"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={40}>40</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  )}
                  {currentSession && (
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      currentSession.isActive
                        ? 'bg-green-900 text-green-300 light:bg-green-100 light:text-green-800'
                        : 'bg-gray-700 text-gray-300 light:bg-gray-200 light:text-gray-700'
                    }`}>
                      {currentSession.isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </div>
              </div>

              {currentSession ? (
                sortedChatters.length > 0 ? (
                  <>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {paginatedChatters.map((chatter) => (
                        <div
                          key={chatter.id}
                          className="chatter-item"
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center space-x-4 flex-1">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full overflow-hidden bg-gray-600 light:bg-gray-300 flex items-center justify-center">
                                  {chatter.profileImage ? (
                                    <img 
                                      src={chatter.profileImage} 
                                      alt={chatter.displayName} 
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-sm font-medium text-white light:text-gray-700">
                                      {chatter.displayName.charAt(0).toUpperCase()}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white light:text-gray-900 truncate">
                                  {chatter.displayName}
                                </p>
                                <p className="text-xs text-gray-500 light:text-gray-500">
                                  {formatDistanceToNow(new Date(chatter.firstSeenAt), { addSuffix: true })}
                                  {chatter.messageCount > 1 && ` • ${chatter.messageCount} messages`}
                                </p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-4">
                              <button
                                onClick={() => handleToggleGreet(chatter.id, !chatter.isGreeted)}
                                className={`w-12 h-10 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                                  chatter.isGreeted
                                    ? recentlyGreeted.has(chatter.id)
                                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                                      : 'bg-green-600 text-white hover:bg-green-700'
                                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500 hover:text-white light:bg-gray-300 light:text-gray-700 light:hover:bg-gray-400 light:hover:text-gray-900'
                                }`}
                              >
                                {chatter.isGreeted
                                  ? recentlyGreeted.has(chatter.id)
                                    ? (
                                        <span 
                                          className="inline-block"
                                          style={{
                                            animation: 'wave 0.8s ease-in-out infinite',
                                            transformOrigin: 'bottom center'
                                          }}
                                        >👋</span>
                                      )
                                    : <span>😊</span>
                                  : <span>⏳</span>
                                }
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700 light:border-gray-200">
                        <div className="text-sm text-gray-400 light:text-gray-600">
                          Showing {startIndex + 1}-{Math.min(endIndex, sortedChatters.length)} of {sortedChatters.length} chatters
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="p-1 rounded border border-gray-600 light:border-gray-300 text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeftIcon className="h-3 w-3" />
                          </button>
                          <div className="flex items-center space-x-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => 
                                page === 1 || 
                                page === totalPages || 
                                (page >= currentPage - 1 && page <= currentPage + 1)
                              )
                              .map((page, index, array) => (
                                <React.Fragment key={page}>
                                  {index > 0 && array[index - 1] !== page - 1 && (
                                    <span className="text-gray-400 light:text-gray-600 px-1 text-xs">...</span>
                                  )}
                                  <button
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      currentPage === page
                                        ? 'bg-twitch-600 text-white'
                                        : 'text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100'
                                    }`}
                                  >
                                    {page}
                                  </button>
                                </React.Fragment>
                              ))
                            }
                          </div>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className="p-1 rounded border border-gray-600 light:border-gray-300 text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRightIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center text-gray-400 light:text-gray-600 py-8">
                    <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No chatters yet. Start the session to begin monitoring!</p>
                  </div>
                )
              ) : (
                <div className="text-center text-gray-400 light:text-gray-600 py-8">
                  <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a session to view chatters</p>
                  {sessions.length > 0 && (
                    <p className="text-sm mt-2">Click the eye icon <EyeIcon className="h-4 w-4 inline text-gray-500 light:text-gray-600" /> next to any session above</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CSS for waving animation */}
        <style>{`
          @keyframes wave {
            0%, 100% { transform: rotate(-15deg); }
            50% { transform: rotate(15deg); }
          }
        `}</style>

        {/* Delete Session Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ ...deleteConfirm, isOpen: false })}
          onConfirm={handleDeleteSession}
          title="Delete Session"
          message={`Are you sure you want to delete "${deleteConfirm.sessionName}"? This action cannot be undone.`}
          confirmText="Delete Session"
          cancelText="Cancel"
          type="danger"
          details={[
            `Session: ${deleteConfirm.sessionName}`,
            `${deleteConfirm.chatterCount} chatters will be permanently deleted`,
            'All chat history and greet status will be lost'
          ]}
        />
      </div>
      
      <Footer />
    </>
  );
};

export default DashboardPage; 